import type { ActionCard } from '@shared/definitions/action-card.model';

/**
 * Scenarios the system generates fully pre-linked (property + contract + amount
 * + recipient all resolved deterministically server-side). For these, the owner
 * never needs to verify the linkage or step through a workflow — the only
 * question is "send it / defer it / drop it". The cockpit therefore offers inline
 * quick actions and a light read-only preview instead of the detail panel or the
 * full-screen workflow stepper.
 *
 * Kept as an explicit allowlist (NOT derived from intent/source) so the simpler
 * UI can never accidentally swallow a workflow-bearing scenario — e.g.
 * `reminder.rent_overdue` (the dunning ladder) is intentionally absent: it
 * carries a real multi-step workflow and must keep the stepper.
 */
export const SIMPLE_SYSTEM_SCENARIO_IDS: ReadonlySet<string> = new Set([
  'reminder.rent_arrears',
  'reminder.rent_due_soon',
  'reminder.indexation_anniversary',
  'reminder.charges_overdue',
  'reminder.insurance_certificate_annual',
  'reminder.boiler_inspection_due',
  'loan.maturity_due',
]);

/**
 * True when a card is a simple, pre-linked system card that should be handled
 * with inline quick actions + a light preview rather than the detail panel /
 * workflow stepper.
 *
 * Hard guards, in order:
 * - a card carrying a workflow is never "simple" (it has steps to walk);
 * - an inbound human email is never a system card;
 * - otherwise the scenario id must be in {@link SIMPLE_SYSTEM_SCENARIO_IDS}.
 */
export function isSimpleSystemCard(card: ActionCard): boolean {
  if (card.workflow?.steps?.length) return false;
  if (card.source === 'INBOUND_EMAIL' && card.senderRole !== 'system') return false;
  const scenarioId = card.classification?.scenarioId;
  return !!scenarioId && SIMPLE_SYSTEM_SCENARIO_IDS.has(scenarioId);
}

/**
 * True when the inline quick actions (Confirm & send / Defer / Dismiss) should
 * be offered on the card — i.e. it's a simple system card AND still in a state
 * the owner can act on. A parked (`waiting_external`) card shows its waiting
 * notice instead; resolved cards show nothing.
 */
export function supportsInlineActions(card: ActionCard): boolean {
  return (
    isSimpleSystemCard(card) &&
    (card.status === 'pending' || card.status === 'scheduled_auto')
  );
}
